import type * as React from 'react';
import styled, { css } from 'styled-components';

import { displayFlagMap, genderMap } from '@xing-com/crate-utils-x1-data';
import { Flag } from '@xing-com/flag';
import { useViewerData } from '@xing-com/hub';
import { Link } from '@xing-com/link';
import { ProfileImage } from '@xing-com/profile-image';
import { ProgressBadge } from '@xing-com/progress-badge';
import {
  scale010,
  scale100,
  spaceM,
  spaceS,
  spaceXS,
  xdlColorBackground,
  xdlColorDisabledSoft,
  xdlColorText,
} from '@xing-com/tokens';
import { Headline } from '@xing-com/typography';
import { addParams } from '@xing-com/xing-tracking';

type To =
  | string
  | { pathname: string; hash?: string; search?: string; state?: unknown }
  | undefined;

const StyledLink = styled(Link)<{ to: To }>`
  background-color: ${xdlColorBackground};
  color: ${xdlColorText};
  &:hover,
  &:focus {
    text-decoration: none;
  }
`;

const StyledInnerProfile = styled.div<{
  $collapsed?: boolean;
  $hasProfileProgressIndicator?: boolean;
  $isOnMeMenu?: boolean;
}>`
  display: flex;
  margin: 0;
  align-items: center;

  ${({ $hasProfileProgressIndicator, $collapsed, $isOnMeMenu }) => css`
    align-items: ${($hasProfileProgressIndicator && !$collapsed) || $isOnMeMenu
      ? 'start'
      : 'center'};
  `}

  ${({ $collapsed }) =>
    $collapsed
      ? css`
          flex-direction: column;
          justify-content: center;
        `
      : css`
          flex-direction: row;
          justify-content: flex-start;
        `}
`;

const StyledProfileImage = styled(ProfileImage)<{
  $collapsed?: boolean;
}>`
  display: block;
  ${({ $collapsed }) =>
    $collapsed
      ? css`
          margin: 0 0 ${spaceS} 0;
        `
      : css`
          margin-right: ${spaceM};
        `}
`;

const StyledFigcaption = styled.figcaption<{
  $hasProfileProgressIndicator?: boolean;
  $collapsed?: boolean;
}>`
  display: flex;
  flex-direction: column;

  ${({ $collapsed, $hasProfileProgressIndicator }) =>
    $collapsed &&
    $hasProfileProgressIndicator &&
    css`
      margin-top: ${spaceS};
    `}
`;

const StyledMeMenuUserName = styled(Headline)<{
  $displayName?: string;
}>`
  ${({ $displayName }) => {
    return $displayName
      ? css`
          margin-top: ${spaceS};
        `
      : css`
          background: ${xdlColorDisabledSoft};
          border-radius: ${scale010};
          height: ${scale100};
        `;
  }}
`;

const StyledUserName = styled.p<{
  $displayName?: string;
  $collapsed?: boolean;
}>`
  ${({ $collapsed, $displayName }) =>
    $collapsed
      ? css`
          display: none;
        `
      : $displayName
        ? css`
            font-weight: bold;
            margin: ${spaceXS} 0 0;
            overflow: hidden;
            text-overflow: ellipsis;
          `
        : css`
            background: ${xdlColorDisabledSoft};
            border-radius: ${scale010};
            height: ${scale100};
          `}
`;

const StyledProgressBadge = styled(ProgressBadge)`
  width: fit-content;
`;

const StyledFlag = styled(Flag)`
  width: fit-content;
`;

export type MeProps = {
  className?: string;
  collapsed?: boolean;
  isOnMeMenu?: boolean;
  tracking: Record<string, string>;
  href: string;
  hasProfileProgressIndicator?: boolean;
};

export const Me: React.FC<MeProps> = ({
  className,
  collapsed = false,
  tracking,
  href,
  hasProfileProgressIndicator,
  isOnMeMenu = false,
}) => {
  const viewerData = useViewerData();
  const user = viewerData?.data?.user;

  const displayName = user?.displayName;
  const userFlag = user?.userFlags?.displayFlag;
  const image = user?.profileImage?.[0]?.url;
  const flagVariant = displayFlagMap(userFlag);

  const networkEfficiencyIndicator =
    viewerData?.data?.networkEfficiencyIndicator || {};
  const { score, userTypeSupported } = networkEfficiencyIndicator;

  const showProfileFillingIndicator =
    hasProfileProgressIndicator && userTypeSupported && score && score !== 1;

  const trackingProps = !hasProfileProgressIndicator
    ? tracking
    : {
        ...tracking,
        ...(score && { expandNeffi: 'true' }),
      };

  const completeTrackingURL = href && addParams(href, trackingProps);

  return (
    <StyledLink
      className={className}
      to={completeTrackingURL}
      data-testid="layout-me"
    >
      <StyledInnerProfile
        $collapsed={collapsed}
        $hasProfileProgressIndicator={hasProfileProgressIndicator}
        $isOnMeMenu={isOnMeMenu}
      >
        <StyledProfileImage
          $collapsed={collapsed}
          size={collapsed ? 'small' : 'medium'}
          profileName={displayName}
          src={image}
          type={genderMap(user?.gender)}
          progressBadge={
            collapsed && showProfileFillingIndicator ? (
              <ProgressBadge
                progress={Math.round(score * 100)}
                data-testid="me-profile-progress-badge-collapsed"
              />
            ) : undefined
          }
        />

        <StyledFigcaption
          $collapsed={collapsed}
          $hasProfileProgressIndicator={hasProfileProgressIndicator}
        >
          {flagVariant && (
            <StyledFlag
              data-testid="me-badge"
              variant={flagVariant}
              size={isOnMeMenu ? 'small' : 'medium'}
            />
          )}
          {isOnMeMenu ? (
            <StyledMeMenuUserName $displayName={displayName} size="xxlarge">
              {displayName}
            </StyledMeMenuUserName>
          ) : (
            <StyledUserName $collapsed={collapsed} $displayName={displayName}>
              {displayName}
            </StyledUserName>
          )}

          {!collapsed && showProfileFillingIndicator ? (
            <StyledProgressBadge
              progress={Math.round(score * 100)}
              data-testid="me-profile-progress-badge"
            />
          ) : null}
        </StyledFigcaption>
      </StyledInnerProfile>
    </StyledLink>
  );
};
